// app/src/modules/ui/SocialIcons.tsx — the shop's social profiles, one definition
// shared by the public website (footer, contact page, WhatsApp chat badge) and the
// Website admin screen that edits them.
//
// Icons are from Simple Icons (CC0): only the six imported here end up in the
// bundle. Each profile is drawn in its own brand colour on a neutral tile, which
// keeps every logo recognisable in both themes without re-colouring trademarks.
import { siFacebook, siInstagram, siTiktok, siYoutube, siX, siWhatsapp, siGoogle } from "simple-icons";

export type SocialKey = "whatsapp" | "facebook" | "instagram" | "tiktok" | "youtube" | "x" | "google";

export interface SocialDef {
  key: SocialKey;
  label: string;
  path: string;
  /** Brand colour. Black marks (TikTok, X) use currentColor so they show on dark tiles. */
  color: string;
  placeholder: string;
  hint: string;
}

export const SOCIALS: SocialDef[] = [
  { key: "whatsapp", label: "WhatsApp", path: siWhatsapp.path, color: `#${siWhatsapp.hex}`, placeholder: "07432 032444 or +44 7432 032444", hint: "The number customers message. Shown as a chat button on the website." },
  { key: "facebook", label: "Facebook", path: siFacebook.path, color: `#${siFacebook.hex}`, placeholder: "https://facebook.com/sloughscooters", hint: "Your Facebook page link." },
  { key: "instagram", label: "Instagram", path: siInstagram.path, color: `#${siInstagram.hex}`, placeholder: "https://instagram.com/sloughscooters", hint: "Profile link, or just @handle." },
  { key: "tiktok", label: "TikTok", path: siTiktok.path, color: "currentColor", placeholder: "https://tiktok.com/@sloughscooters", hint: "Profile link, or just @handle." },
  { key: "youtube", label: "YouTube", path: siYoutube.path, color: `#${siYoutube.hex}`, placeholder: "https://youtube.com/@sloughscooters", hint: "Channel link." },
  { key: "x", label: "X (Twitter)", path: siX.path, color: "currentColor", placeholder: "https://x.com/sloughscooters", hint: "Profile link, or just @handle." },
  { key: "google", label: "Google reviews", path: siGoogle.path, color: `#${siGoogle.hex}`, placeholder: "https://g.page/r/…/review", hint: "Your Google Business profile or review link." },
];

/** Digits for wa.me: UK 07… becomes 447…; anything with a country code is kept. */
export function waDigits(raw: string): string {
  let d = String(raw || "").replace(/[^\d+]/g, "");
  if (d.startsWith("+")) d = d.slice(1);
  else if (d.startsWith("00")) d = d.slice(2);
  else if (/^0\d{9,10}$/.test(d)) d = "44" + d.slice(1);
  return d.replace(/\D/g, "");
}

/** A usable link from whatever was typed: a full URL, a bare domain, an @handle or a number. */
export function socialHref(key: SocialKey, raw: string, waText?: string): string | null {
  const v = String(raw || "").trim();
  if (!v) return null;
  if (key === "whatsapp") {
    if (/^https?:\/\//i.test(v)) return v;
    const d = waDigits(v);
    if (d.length < 8) return null;
    return `https://wa.me/${d}${waText ? `?text=${encodeURIComponent(waText)}` : ""}`;
  }
  if (/^https?:\/\//i.test(v)) return v;
  const handle = v.replace(/^@/, "");
  const base: Partial<Record<SocialKey, string>> = {
    instagram: "https://instagram.com/", tiktok: "https://www.tiktok.com/@", x: "https://x.com/",
    facebook: "https://facebook.com/", youtube: "https://youtube.com/@",
  };
  if (/^[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(v)) return `https://${v}`;
  return base[key] ? base[key] + handle : null;
}

/** The profiles that have a value, in display order, ready to link. */
export function socialLinks(content: { social?: Record<string, any>; contact?: Record<string, any> }): { def: SocialDef; href: string }[] {
  const s = content.social ?? {}; const c = content.contact ?? {};
  const out: { def: SocialDef; href: string }[] = [];
  for (const def of SOCIALS) {
    const raw = def.key === "whatsapp" ? (s.whatsapp || c.whatsapp) : s[def.key];
    const href = raw ? socialHref(def.key, raw) : null;
    if (href) out.push({ def, href });
  }
  return out;
}

export function SocialIcon({ def, size = 20, mono = false }: { def: SocialDef; size?: number; mono?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false" style={{ flex: "0 0 auto" }}>
      <path d={def.path} fill={mono ? "currentColor" : def.color} />
    </svg>
  );
}

/** A row of round icon links — the footer and contact page use this. */
export function SocialRow({ links, size = 20, tile = 40, bg = "rgba(255,255,255,.08)", gap = 10 }: { links: { def: SocialDef; href: string }[]; size?: number; tile?: number; bg?: string; gap?: number }) {
  if (!links.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap }}>
      {links.map(({ def, href }) => (
        <a key={def.key} href={href} target="_blank" rel="noopener noreferrer" aria-label={`${def.label} (opens in a new tab)`} title={def.label}
          style={{ width: tile, height: tile, borderRadius: 999, display: "inline-grid", placeItems: "center", background: bg, color: "inherit" }}>
          <SocialIcon def={def} size={size} />
        </a>
      ))}
    </div>
  );
}
